import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';

export const imageShape = {
    cropped     : PropTypes.bool,
    title       : PropTypes.string,
    url         : PropTypes.string,
    description : PropTypes.string,
};

export const specsShape = {
    maxOccupancy : PropTypes.number,
    size         : PropTypes.number,
    sizeUnit     : PropTypes.string,
    beds         : PropTypes.array,
};

export const amenityShape = {
    code        : PropTypes.string,
    text        : PropTypes.string,
    icon        : PropTypes.string,
    defaultIcon : PropTypes.string,
};

export const currencyShape = {
    code   : PropTypes.string.isRequired,
    symbol : PropTypes.string.isRequired,
};

export const queryParamsShape = {
    domain               : PropTypes.string,
    'skd-checkin'        : PropTypes.string,
    'skd-checkout'       : PropTypes.string,
    'skd-language-code'  : PropTypes.string,
    'skd-currency-code'  : PropTypes.string,
    'skd-promotion-code' : PropTypes.string,
    adult_room           : PropTypes.string,
    child_room           : PropTypes.string,
    'skd-total-rooms'    : PropTypes.string,
};

export const queryParamsReadableShape = {
    Checkin          : PropTypes.string,
    Checkout         : PropTypes.string,
    'Currency-code'  : PropTypes.string,
    'Language-code'  : PropTypes.string,
    'Total-rooms'    : PropTypes.string,
    'Widget-section' : PropTypes.string,
    'Adult-room1'    : PropTypes.string,
};

export const multiPropertyOfferShape = {
    code          : PropTypes.string.isRequired,
    price         : PropTypes.number.isRequired,
    originalPrice : PropTypes.number,
    discount      : PropTypes.number,
};

export const occupancyShape = {
    adults   : PropTypes.number,
    children : PropTypes.arrayOf(PropTypes.number),
};

export const searchParamsShape = {
    checkIn   : PropTypes.string,
    checkOut  : PropTypes.string,
    occupancy : PropTypes.arrayOf(PropTypes.shape(occupancyShape)),
    promoCode : PropTypes.string,
};

export const headerShapes = {
    imageShape,
    currencyShape,
};

export const settingsShape = {
    maxCheckOutDate    : PropTypes.string,
    childrenEnabled    : PropTypes.bool,
    minOccupancy       : PropTypes.number,
    maxOccupancy       : PropTypes.number,
    maxChildAge        : PropTypes.number,
    minChildAge        : PropTypes.number,
    maxRoomsPerBooking : PropTypes.number,
};

export const searchShape = {
    checkIn             : PropTypes.string,
    checkOut            : PropTypes.string,
    searchButtonEnabled : PropTypes.bool,
    language            : PropTypes.string,
};

export const configShape = {
    id              : PropTypes.string,
    isSetDisabled   : PropTypes.bool,
    domain          : PropTypes.string,
    currencyDisplay : PropTypes.string,
    language        : PropTypes.string,
    currency        : PropTypes.string,
    filterByPrice   : PropTypes.bool,
    sortOrder       : PropTypes.string,
};

export const searchBarShape = {
    showCurrencySelector   : PropTypes.bool,
    showLanguageSelector   : PropTypes.bool,
    promotionalCodeEnabled : PropTypes.bool,
    searchButtonEnabled    : PropTypes.bool,
    fullWidth              : PropTypes.bool,
    isConnected            : PropTypes.bool,
    isDisabled             : PropTypes.bool,
    isRendered             : PropTypes.bool,
    container              : PropTypes.string,
};

export const rateShape = {
    layout          : PropTypes.number,
    loadingBlocks   : PropTypes.number,
    showDescription : PropTypes.bool,
    showSpecs       : PropTypes.bool,
    container       : PropTypes.string,
    showFilters     : PropTypes.bool,
    sortOrder       : PropTypes.string,
    filterByCode    : PropTypes.string,
    isDisabled      : PropTypes.bool,
    isRendered      : PropTypes.bool,
};

export const offerShape = {
    layout          : PropTypes.number,
    loadingBlocks   : PropTypes.number,
    showDescription : PropTypes.bool,
    showSpecs       : PropTypes.bool,
    container       : PropTypes.string,
    showTeaser      : PropTypes.bool,
    showFilters     : PropTypes.bool,
    sortOrder       : PropTypes.string,
    filterByCode    : PropTypes.string,
    isDisabled      : PropTypes.bool,
    isRendered      : PropTypes.bool,
};

export const widgetPoolShape = {
    config    : PropTypes.shape(configShape),
    searchbar : PropTypes.shape(searchBarShape),
    rates     : PropTypes.shape(rateShape),
    offers    : PropTypes.shape(offerShape),
};

export const colWidthShape = {
    base       : PropTypes.number,
    small      : PropTypes.number,
    medium     : PropTypes.number,
    large      : PropTypes.number,
    extraLarge : PropTypes.number,
};

export const layoutsDefaultProps = {
    code            : '',
    title           : '',
    images          : [ {} ],
    description     : null,
    maxOccupancy    : null,
    size            : null,
    sizeUnit        : null,
    teaser          : null,
    price           : null,
    isOffer         : false,
    showDescription : false,
    showSpecs       : false,
    showTeaser      : false,
};

export const layoutsPropTypes = {
    code              : PropTypes.string,
    currency          : PropTypes.shape(currencyShape).isRequired,
    currencyDisplay   : PropTypes.string.isRequired,
    language          : PropTypes.string.isRequired,
    images            : PropTypes.arrayOf(PropTypes.shape(imageShape)),
    maxOccupancy      : PropTypes.number,
    price             : PropTypes.number,
    description       : PropTypes.string,
    onSearchResults   : PropTypes.func.isRequired,
    size              : PropTypes.number,
    sizeUnit          : PropTypes.string,
    title             : PropTypes.string,
    teaser            : PropTypes.string,
    isOffer           : PropTypes.bool,
    showDescription   : PropTypes.bool,
    showSpecs         : PropTypes.bool,
    showTeaser        : PropTypes.bool,
    shouldUpgradeTier : PropTypes.bool.isRequired,
    intl              : intlShape.isRequired,
};

export const marketingShape = {
    type    : PropTypes.string,
    content : PropTypes.oneOfType([ PropTypes.string, PropTypes.node ]),
};

export const locationShape = {
    address   : PropTypes.string,
    latitude  : PropTypes.number,
    longitude : PropTypes.number,
    city      : PropTypes.string,
    country   : PropTypes.string,
};

export const priceInfoShape = {
    discount      : PropTypes.number,
    price         : PropTypes.number,
    originalPrice : PropTypes.number,
};

export const propertyLocationShape = {
    latitude  : PropTypes.number,
    longitude : PropTypes.number,
    address   : PropTypes.string,
    country   : PropTypes.string,
    city      : PropTypes.string,
};

export const markerPositionShape = {
    lat : PropTypes.number.isRequired,
    lng : PropTypes.number.isRequired,
};

export const hotelsOptionsShape = {
    label : PropTypes.string,
    value : PropTypes.string,
};

export const destinationOptionsShape = {
    label    : PropTypes.string,
    value    : PropTypes.string,
    children : PropTypes.arrayOf(PropTypes.shape()),
};

export const regionsHotelsShape = {
    countries : PropTypes.arrayOf(destinationOptionsShape),
    hotels    : PropTypes.arrayOf(PropTypes.shape()),
};

export const paginationShape = {
    currentPage  : PropTypes.number,
    totalPages   : PropTypes.number,
    pageSize     : PropTypes.number,
    totalResults : PropTypes.number,
};

export const propertyShape = {
    amenities   : PropTypes.arrayOf(PropTypes.shape(amenityShape)),
    code        : PropTypes.string.isRequired,
    description : PropTypes.string,
    location    : PropTypes.shape(locationShape),
    mainImage   : PropTypes.shape(imageShape),
    marketing   : PropTypes.shape({
        mealPlan  : PropTypes.string,
        promotion : PropTypes.bool,
    }),
    name       : PropTypes.string.isRequired,
    price      : PropTypes.number,
    stars      : PropTypes.number,
    trustYouId : PropTypes.string,
};

export const trustYouProfileDataShape = {
    name              : PropTypes.string.isRequired,
    score             : PropTypes.string.isRequired,
    sources_count     : PropTypes.number.isRequired,
    reviews_count     : PropTypes.number.isRequired,
    ty_id             : PropTypes.string.isRequired,
    score_description : PropTypes.string.isRequired,
};

export const trustYouProfileShape = {
    hasError  : PropTypes.bool.isRequired,
    isLoading : PropTypes.bool.isRequired,
    language  : PropTypes.string.isRequired,
    data      : PropTypes.shape(trustYouProfileDataShape),
};

export const trustYouReviewsDataShape = {
    content : PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    stars   : PropTypes.number.isRequired,
};

export const trustYouReviewsShape = {
    hasError  : PropTypes.bool.isRequired,
    isLoading : PropTypes.bool.isRequired,
    language  : PropTypes.string.isRequired,
    data      : PropTypes.arrayOf(PropTypes.shape(trustYouReviewsDataShape)),
};

export const trustYouReducerShape = {
    profile : PropTypes.shape(trustYouProfileShape),
    reviews : PropTypes.shape(trustYouReviewsShape),
};

export const translationsReducerShape = {
    hasError  : PropTypes.bool,
    isLoading : PropTypes.bool.isRequired,
    data      : PropTypes.objectOf(PropTypes.string),
};

export const propertyListContainerConfigShape = {
    container           : PropTypes.string,
    isDisabled          : PropTypes.bool,
    isRendered          : PropTypes.bool,
    layout              : PropTypes.string,
    page                : PropTypes.number,
    pageSize            : PropTypes.number,
    priceCalcMode       : PropTypes.string,
    priceDisplayMode    : PropTypes.string,
    showFeedbackMessage : PropTypes.bool,
    showUnavailable     : PropTypes.bool,

};

export const propertiesFiltersShape = {
    minPrice     : PropTypes.number,
    maxPrice     : PropTypes.number,
    stars        : PropTypes.arrayOf(PropTypes.string),
    propertyName : PropTypes.string,
};

export const propertiesAutocompleteShape = {
    visible                        : PropTypes.bool,
    types                          : PropTypes.arrayOf(PropTypes.string),
    citySearchDistanceMode         : PropTypes.oneOf([ 'DYNAMIC', 'FIXED', 'EXACT' ]),
    citySearchBucketsStopCount     : PropTypes.number,
    citySearchDistanceBucketsMiles : PropTypes.arrayOf(PropTypes.number),
    citySearchDistanceKm           : PropTypes.number,
    citySearchDistanceMiles        : PropTypes.number,
    distanceUnitOfMeasure          : PropTypes.oneOf([ 'MILES', 'KM', 'MILES_AND_KM' ]),
};
